import { useCallback, useEffect, useState } from 'react'

/** The period a cost is carried over: how many months, and how the volume grows across them
 *  (a percentage a month, or a volume to reach by the last month). */
export interface Period { months: number; growth: number; end: number | null }

const KEY = 'period'
const MONTHS = [12, 24, 36]
const GROWTH = [0, 3, 5, 10]
const fresh = (): Period => ({ months: 24, growth: 0, end: null })

function load(): Period {
  try {
    const got = JSON.parse(localStorage.getItem(KEY) ?? 'null') as Partial<Period> | null
    if (got && MONTHS.includes(got.months as number) && typeof got.growth === 'number') {
      return { months: got.months as number, growth: got.growth, end: typeof got.end === 'number' && got.end > 0 ? got.end : null }
    }
  } catch { /* storage blocked or garbage: start fresh */ }
  return fresh()
}

/** The shared period. Every page reads the same stored value, as they do the region choice. */
export function usePeriod(): [Period, (next: Period) => void] {
  const [period, setState] = useState<Period>(load)
  useEffect(() => {
    const onStorage = (e: StorageEvent) => { if (e.key === KEY) setState(load()) }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])
  const set = useCallback((next: Period) => {
    setState(next)
    try { localStorage.setItem(KEY, JSON.stringify(next)) } catch { /* storage blocked */ }
  }, [])
  return [period, set]
}

/** The period as the API takes it: a volume to reach by the end wins over the rate. */
export const periodBody = (p: Period, withGrowth = true) => ({
  months: p.months,
  ...(withGrowth ? (p.end ? { end_output_tokens_per_day: p.end } : { growth_pct: p.growth }) : {}),
})

/** "+5% a month", "flat", or "to 50M output tokens a day" */
export const growthWords = (p: Period, compact: (n: number) => string) =>
  p.end ? `to ${compact(p.end)} output tokens a day` : p.growth ? `+${p.growth}% a month` : 'flat volume'

/** The Time step of a setup panel: the period, and (where a volume is priced) how it grows. */
export function TimeStep({ value, onChange, growth = true }: { value: Period; onChange: (next: Period) => void; growth?: boolean }) {
  return (
    <div className="timestep" data-tour="time">
      <div className="field">
        <label>Period</label>
        <div className="chips" role="group" aria-label="Period">
          {MONTHS.map((m) => (
            <button key={m} type="button" aria-pressed={value.months === m} onClick={() => onChange({ ...value, months: m })}>{m} months</button>
          ))}
        </div>
      </div>
      {growth ? (
        <>
          <div className="field">
            <label>Volume growth a month</label>
            <div className="chips" role="group" aria-label="Volume growth a month">
              {GROWTH.map((g) => (
                <button key={g} type="button" aria-pressed={!value.end && value.growth === g}
                  onClick={() => onChange({ ...value, growth: g, end: null })}>{g ? `+${g}%` : 'Flat'}</button>
              ))}
            </div>
          </div>
          <div className="field">
            <label htmlFor="period-end">or reach, output tokens a day by the end</label>
            <input id="period-end" type="number" min={0} step={1000000} inputMode="numeric" placeholder="50000000"
              value={value.end ?? ''} onChange={(e) => { const n = Number(e.target.value); onChange({ ...value, end: n > 0 ? n : null }) }} />
          </div>
        </>
      ) : null}
    </div>
  )
}
